import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";

const navItems = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/products", label: "Products", testid: "nav-products-link" },
  { to: "/process", label: "Our Process", testid: "nav-process-link" },
  { to: "/condition-guide", label: "Condition Guide", testid: "nav-condition-link" },
  { to: "/returns", label: "Returns & Support", testid: "nav-returns-link" },
  { to: "/sustainability", label: "Sustainability", testid: "nav-sustainability-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80"
      data-testid="site-header"
    >
      {/* Top utility bar */}
      <div className="hidden md:block border-b border-gray-100 bg-[#0B192C] text-white">
        <div className="cnl-container flex h-9 items-center justify-between text-[12px] tracking-wide">
          <span className="opacity-80">
            Independent Electronics Reseller · Corona, California · Established 2020
          </span>
          <div className="flex items-center gap-5">
            <a
              href="mailto:cnltech@proton.me"
              className="opacity-80 hover:opacity-100 transition-opacity"
              data-testid="top-email-link"
            >
              cnltech@proton.me
            </a>
            <a
              href="tel:+19514792470"
              className="flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity"
              data-testid="top-phone-link"
            >
              <Phone size={12} /> +1 (951) 479-2470
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="cnl-container flex h-16 lg:h-20 items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" aria-label="CNL Tech home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `cnl-link text-[14px] font-medium ${
                  isActive ? "active text-[#0B192C]" : "text-gray-700 hover:text-[#0B192C]"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="inline-flex items-center justify-center rounded-sm border border-[#0B192C] bg-[#0B192C] px-5 py-2.5 text-[13px] font-semibold uppercase tracking-[0.12em] text-white transition-all duration-200 hover:bg-[#1E3E62] hover:border-[#1E3E62]"
          >
            Get In Touch
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-sm border border-gray-300 text-[#0B192C] hover:bg-gray-50"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <nav className="cnl-container py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${item.testid}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-[15px] font-medium rounded-sm transition-colors ${
                    isActive
                      ? "bg-[#0B192C] text-white"
                      : "text-gray-800 hover:bg-gray-100"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center rounded-sm bg-[#0B192C] px-5 py-3 text-[13px] font-semibold uppercase tracking-[0.12em] text-white"
              data-testid="mobile-nav-cta"
            >
              Get In Touch
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
